import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../../lib/LangContext";
import { CONTENT, UI } from "../../lib/content";
import { TID } from "../../lib/testIds";
import { Wordmark } from "../Logo";

// Kinetic hero — masked line-by-line reveal of the wordmark
export default function CoverChapter({ onBegin }) {
    const { lang } = useLang();
    const c = CONTENT.cover[lang];
    const t = UI[lang];

    const { scrollYProgress } = useScroll();
    const yParallax = useTransform(scrollYProgress, [0, 0.2], [0, -80]);

    // Letter-by-letter reveal — INTOLLE + Я + ANTIA (13 chars, mirror at index 7)
    const letters = ["I", "N", "T", "O", "L", "L", "E", "R", "A", "N", "T", "I", "A"];

    return (
        <section
            id="cover"
            data-testid={TID.cover}
            data-chapter="cover"
            className="chapter relative min-h-screen flex flex-col justify-center paper overflow-hidden"
        >
            {/* Top strip */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 1.2 }}
                className="absolute top-24 md:top-28 left-6 md:left-12 right-6 md:right-12 flex justify-between font-micro text-[10px] md:text-[11px]"
                style={{ color: "#0B0B0B", letterSpacing: "0.4em" }}
            >
                <span>{c.eyebrow}</span>
                <span className="hidden md:inline">{t.registered}</span>
                <span>{c.note}</span>
            </motion.div>

            {/* Main mark */}
            <motion.div style={{ y: yParallax }} className="relative flex flex-col items-center justify-center px-4 md:px-8 pt-32 md:pt-24">
                <div data-testid={TID.heroLogo} className="w-full max-w-[1400px] mx-auto">
                    <h1
                        aria-label="INTOLLERANTIA"
                        className="font-didone font-normal text-center leading-[0.85]"
                        style={{
                            fontSize: "clamp(3.2rem, 12vw, 12rem)",
                            letterSpacing: "0.02em",
                            color: "#0B0B0B",
                        }}
                    >
                        <span className="inline-flex overflow-hidden align-baseline">
                            {letters.map((ch, i) => {
                                const isR = i === 7;
                                return (
                                    <motion.span
                                        key={i}
                                        initial={{ y: "110%", opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{
                                            delay: 0.5 + i * 0.06,
                                            duration: 0.9,
                                            ease: [0.7, 0, 0.2, 1],
                                        }}
                                        className="inline-block"
                                    >
                                        {isR ? (
                                            <span
                                                aria-hidden="true"
                                                className="inline-block"
                                                style={{ transform: "scaleX(-1)" }}
                                            >
                                                {ch}
                                            </span>
                                        ) : (
                                            ch
                                        )}
                                    </motion.span>
                                );
                            })}
                        </span>
                    </h1>

                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 1.8, duration: 1.2, ease: [0.7, 0, 0.2, 1] }}
                        className="h-px w-56 md:w-96 mx-auto mt-8 md:mt-12"
                        style={{ background: "#C9A96A", transformOrigin: "center" }}
                    />
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 2.1, duration: 1.2 }}
                        className="text-center mt-6 md:mt-8 font-micro text-[10px] md:text-xs"
                        style={{ color: "#0B0B0B", letterSpacing: "0.48em" }}
                    >
                        {c.payoff.toUpperCase()}
                    </motion.div>
                </div>
            </motion.div>

            {/* Bottom scroll hint / begin buttons */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.6, duration: 1 }}
                className="absolute bottom-10 left-6 right-6 md:left-12 md:right-12 flex flex-col md:flex-row items-center justify-between gap-4"
            >
                <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", opacity: 0.7 }}>
                    {lang === "en" ? "VOL. I · 21 CHAPTERS" : "VOL. I · 21 CAPITOLI"}
                </div>
                <button
                    data-testid={TID.beginScroll}
                    onClick={onBegin}
                    className="group font-micro text-[10px] md:text-xs flex items-center gap-3"
                    style={{ letterSpacing: "0.4em" }}
                >
                    <span className="link-ed">{t.scroll.toUpperCase()}</span>
                    <motion.span
                        animate={{ y: [0, 6, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="inline-block"
                    >
                        ↓
                    </motion.span>
                </button>
                <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", opacity: 0.7 }}>
                    {t.registered}
                </div>
            </motion.div>
        </section>
    );
}
